import { useEffect } from 'react';

/**
 * Defeats long-uptime SPA decay on the family tablet display.
 * After ~48h the JS event loop wedges (rAF stops, byte-identical screenshots)
 * without OOM — accumulated timers/stale promises/wedged WebSockets. A full
 * reload clears it. Safe because this app has no unsaved client-side state.
 *
 * Trigger: page has been continuously loaded for >6 hours (wall-clock) — reload
 * to clear long-uptime decay.
 *
 * NOTE: a previous second trigger (reload whenever the tab became visible after
 * being backgrounded) was REMOVED. On this kiosk the screen cycles on/off
 * constantly via motion detection, so reloading on every wake caused a visible
 * reload flash + churn and masked TimeTile's instant clock refresh. Heartbeat
 * instrumentation showed no per-session decay, so the per-wake reload is
 * unnecessary; the 6h age reload here + the 30-min native Fully Kiosk reload
 * remain the decay safety nets, and TimeTile self-corrects the clock on wake.
 *
 * Runs unconditionally in production builds (this is a dedicated tablet-kiosk
 * display — the safety nets should always be on). Skipped in dev so HMR works.
 */

const SIX_HOURS_MS = 6 * 60 * 60 * 1000;

export function useKioskAutoReload(): void {
  useEffect(() => {
    if (import.meta.env.DEV) return;

    // Page age exceeds 6 hours — schedule a reload at that threshold.
    const ageMs = Date.now() - performance.timeOrigin;
    const remainingMs = SIX_HOURS_MS - ageMs;

    if (remainingMs <= 0) {
      // Already past threshold on mount — reload immediately.
      window.location.reload();
      return;
    }

    const ageTimer = window.setTimeout(() => window.location.reload(), remainingMs);

    return () => {
      window.clearTimeout(ageTimer);
    };
  }, []); // empty deps — intentional, this effect runs once on mount
}
